/**
 * Shared, dependency-free Care Guide PDF exporter.
 *
 * Both the library modal and a canonical /pages/care-guides/<handle> page keep
 * the advanced guide in a data-care-template element. This helper walks that
 * same markup so the two reader entry paths download the same document.
 */
(function(global){
  'use strict';

  if (global.BZCareGuidePdf) return;

  function templateFor(root, slug){
    if (!root || !slug) return null;
    return root.querySelector('[data-care-template][data-care-version="advanced"][data-care-slug="' + slug + '"]');
  }

  function fileNameFor(slug){
    return 'big-zs-advanced-care-guide-' + slug + '.pdf';
  }

  function pdfText(value){
    var text = String(value || '');
    var map = {
      '\u2013': '-',
      '\u2014': '-',
      '\u2018': "'",
      '\u2019': "'",
      '\u201c': '"',
      '\u201d': '"',
      '\u2022': '-',
      '\u00b0': ' degrees ',
      '\u00d7': 'x',
      '\u00a0': ' '
    };
    text = text.replace(/[\u2013\u2014\u2018\u2019\u201c\u201d\u2022\u00b0\u00d7\u00a0]/g, function(char){
      return map[char] || ' ';
    });
    if (text.normalize) text = text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    return text.replace(/[^\x09\x0A\x0D\x20-\x7E]/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function pdfEscape(value){
    return pdfText(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
  }

  function readCleanText(element){
    return pdfText(element ? element.textContent : '');
  }

  function pushPdfLine(lines, text, style){
    text = pdfText(text);
    if (!text) return;
    lines.push({
      text: text,
      size: style && style.size ? style.size : 10,
      font: style && style.font ? style.font : 'F1',
      gap: style && style.gap ? style.gap : 4,
      indent: style && style.indent ? style.indent : 0
    });
  }

  function extractPdfLines(options){
    var template = templateFor(options.root, options.slug);
    if (!template) return [];

    var holder = document.createElement('div');
    holder.appendChild(template.content.cloneNode(true));
    if (typeof options.normalize === 'function') options.normalize(holder);

    var guide = holder.querySelector('.bz-care') || holder;
    var meta = options.meta || {};
    var lines = [];

    pushPdfLine(lines, (meta.title || 'Care Guide') + ' Advanced Care Guide', { size: 18, font: 'F2', gap: 10 });
    if (meta.species) pushPdfLine(lines, meta.species, { size: 12, font: 'F2', gap: 12 });

    Array.prototype.slice.call(guide.children).forEach(function(child){
      var tag = child.tagName;
      if (tag === 'H1') return;
      if (child.classList && child.classList.contains('bz-care__species-title')) return;
      if (tag === 'H2') {
        var heading = readCleanText(child);
        if (heading && heading.indexOf('Advanced Care Guide') === -1) {
          pushPdfLine(lines, heading, { size: 14, font: 'F2', gap: 7 });
        }
        return;
      }
      if (tag === 'H3') {
        pushPdfLine(lines, readCleanText(child), { size: 12, font: 'F2', gap: 5 });
        return;
      }
      if (tag === 'P') {
        pushPdfLine(lines, readCleanText(child), { size: 10, gap: 6 });
        return;
      }
      if (tag === 'TABLE') {
        Array.prototype.slice.call(child.querySelectorAll('tr')).forEach(function(row){
          var cells = Array.prototype.slice.call(row.children).map(readCleanText).filter(Boolean);
          if (cells.length) pushPdfLine(lines, cells.join(': '), { size: 10, gap: 3 });
        });
        return;
      }
      if (tag === 'UL') {
        Array.prototype.slice.call(child.children).forEach(function(item){
          pushPdfLine(lines, '- ' + readCleanText(item), { size: 10, gap: 3, indent: 10 });
        });
        return;
      }
      if (tag === 'DETAILS') {
        pushPdfLine(lines, readCleanText(child.querySelector('summary')), { size: 12, font: 'F2', gap: 5 });
        Array.prototype.slice.call(child.querySelectorAll('li')).forEach(function(item){
          pushPdfLine(lines, '- ' + readCleanText(item), { size: 10, gap: 3, indent: 10 });
        });
        return;
      }
      Array.prototype.slice.call(child.querySelectorAll('h3')).forEach(function(heading){
        pushPdfLine(lines, readCleanText(heading), { size: 12, font: 'F2', gap: 5 });
      });
      Array.prototype.slice.call(child.querySelectorAll('li')).forEach(function(item){
        pushPdfLine(lines, '- ' + readCleanText(item), { size: 10, gap: 3, indent: 10 });
      });
    });

    return lines;
  }

  function wrapPdfText(text, maxChars){
    var words = pdfText(text).split(' ');
    var lines = [];
    var current = '';
    words.forEach(function(word){
      var next = current ? current + ' ' + word : word;
      if (next.length > maxChars && current) {
        lines.push(current);
        current = word;
      } else {
        current = next;
      }
    });
    if (current) lines.push(current);
    return lines;
  }

  function buildPdfBlob(lines){
    var pageWidth = 612;
    var pageHeight = 792;
    var margin = 54;
    var usableWidth = pageWidth - margin * 2;
    var y = pageHeight - margin;
    var pages = [[]];

    function newPage(){
      pages.push([]);
      y = pageHeight - margin;
    }

    function addText(text, style){
      var size = style.size || 10;
      var leading = Math.max(size + 3, 12);
      var indent = style.indent || 0;
      var maxChars = Math.max(28, Math.floor((usableWidth - indent) / (size * .48)));
      wrapPdfText(text, maxChars).forEach(function(line, index){
        if (y < margin + leading) newPage();
        pages[pages.length - 1].push('BT /' + style.font + ' ' + size + ' Tf ' + (margin + indent) + ' ' + y + ' Td (' + pdfEscape(line) + ') Tj ET');
        y -= leading;
        if (index === 0 && style.gap) y -= style.gap;
      });
    }

    lines.forEach(function(line){ addText(line.text, line); });

    var objects = [];
    objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
    objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';
    objects[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>';

    var nextId = 5;
    var kids = [];
    pages.forEach(function(pageLines){
      var stream = pageLines.join('\n');
      var contentId = nextId++;
      var pageId = nextId++;
      objects[contentId] = '<< /Length ' + stream.length + ' >>\nstream\n' + stream + '\nendstream';
      objects[pageId] = '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + pageWidth + ' ' + pageHeight + '] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ' + contentId + ' 0 R >>';
      kids.push(pageId + ' 0 R');
    });
    objects[2] = '<< /Type /Pages /Kids [' + kids.join(' ') + '] /Count ' + kids.length + ' >>';

    var output = "%PDF-1.4\n% Big Z's Care Guide PDF\n";
    var offsets = [0];
    for (var i = 1; i < objects.length; i += 1) {
      offsets[i] = output.length;
      output += i + ' 0 obj\n' + objects[i] + '\nendobj\n';
    }
    var xrefAt = output.length;
    output += 'xref\n0 ' + objects.length + '\n0000000000 65535 f \n';
    for (var j = 1; j < objects.length; j += 1) {
      output += ('0000000000' + offsets[j]).slice(-10) + ' 00000 n \n';
    }
    output += 'trailer\n<< /Size ' + objects.length + ' /Root 1 0 R >>\nstartxref\n' + xrefAt + '\n%%EOF';
    return new Blob([output], { type: 'application/pdf' });
  }

  function download(options){
    options = options || {};
    var lines = extractPdfLines(options);
    if (!lines.length) return false;

    var blob = buildPdfBlob(lines);
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = fileNameFor(options.slug);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(function(){ URL.revokeObjectURL(url); }, 250);
    return true;
  }

  global.BZCareGuidePdf = { download: download };
})(window);
